import i18n from 'i18next';

import { RegFieldsEnum } from '../types/working-model';
import { getStorageItem, getStorageObject, removeStorageItem, setStorageItem, setStorageObject } from './local-storage';

export const IN_PERSON_PARAM = 'in-person';
export const IN_PERSON_YES = 'Yes';
export const IN_PERSON_NO = 'No';

// Long enough for any registration session and SSO round trip; after registration the profile answer
// takes over, so this only bounds the pre-registration window.
export const IN_PERSON_TTL_HOURS = 24;

const PENDING_KEY = 'inPerson.pending';

export const inPersonRecordKey = (eventUuid: string): string => `inPerson.${eventUuid}`;

// `param` is what the link said on arrival; `working` is the current effective value.
interface InPersonRecord {
	param: string | null;
	working: string;
}

const FALSY_FLAG_VALUES = new Set(['false', '0', 'no', 'off']);

// Presence means true; an explicit falsy value is respected so a link can be neutralised.
const isTruthyFlag = (value: string | null | undefined): boolean => {
	if (value === null || value === undefined) return false;
	return !FALSY_FLAG_VALUES.has(value.trim().toLowerCase());
};

const readFromSearch = (search: string): string | null => {
	if (!search) return null;
	return new URLSearchParams(search).get(IN_PERSON_PARAM);
};

// Captured at module load because the param is stripped from the URL on mount, which can happen
// before the registration form renders, and the event uuid isn't known yet to key the real record.
if (typeof window !== 'undefined') {
	const bootParam = readFromSearch(window.location.search);
	if (bootParam !== null) {
		setStorageItem(PENDING_KEY, String(isTruthyFlag(bootParam)), IN_PERSON_TTL_HOURS);
	}
}

// Moves a captured param into the event's record. Clearing the pending slot keeps this once-only, so
// a later manual change can't be clobbered by a re-read.
export const captureInPersonParam = (eventUuid?: string | null): boolean | undefined => {
	if (!eventUuid) return undefined;

	const pending = getStorageItem(PENDING_KEY);
	if (pending === null) return undefined;

	removeStorageItem(PENDING_KEY);
	setStorageObject(inPersonRecordKey(eventUuid), { param: pending, working: pending }, IN_PERSON_TTL_HOURS);

	return pending === 'true';
};

// The primary read: the link seeds it, every manual change updates it. Callers fall back to the
// attendee's stored profile answer when this returns undefined.
export const getInPersonWorking = (eventUuid?: string | null): boolean | undefined => {
	if (!eventUuid) return undefined;

	const captured = captureInPersonParam(eventUuid);
	if (captured !== undefined) return captured;

	const record = getStorageObject<InPersonRecord>(inPersonRecordKey(eventUuid));
	if (!record || typeof record.working !== 'string') return undefined;

	return record.working === 'true';
};

// i18n key the reg form resolves the Yes option through - see parseFields' translatedOptions.
const YES_OPTION_KEY = `registration.registration_fields.options.${IN_PERSON_YES}`;

// The answer is stored as the option label the attendee actually picked, and nothing records which
// language that was. So accept the base value or its translation in any language i18n has loaded.
// The `select_<id>` sidecar other select answers carry is not echoed back into the attendee token.
const yesValues = (): Set<string> => {
	const values = new Set([IN_PERSON_YES]);

	for (const language of Object.keys(i18n.store?.data ?? {})) {
		// The bundles are flat and their keys contain dots, so read the map rather than a dotted lookup.
		const translated = i18n.getResourceBundle?.(language, 'homepage')?.[YES_OPTION_KEY];
		if (typeof translated === 'string' && translated) {
			values.add(translated);
		}
	}

	return values;
};

export const isInPersonAnswerYes = (
	channelFields: Record<string, unknown> | null | undefined
): boolean | undefined => {
	if (!channelFields) return undefined;

	const value = channelFields[RegFieldsEnum.attend_in_person];
	if (typeof value !== 'string') return undefined;

	return yesValues().has(value);
};

// Storage, not redux: the answer has to survive the SSO round trip, which reloads the app.
export const setInPersonWorking = (eventUuid: string | null | undefined, isInPerson: boolean): void => {
	if (!eventUuid) return;

	const record = getStorageObject<InPersonRecord>(inPersonRecordKey(eventUuid));

	setStorageObject(
		inPersonRecordKey(eventUuid),
		{ param: record?.param ?? null, working: String(isInPerson) },
		IN_PERSON_TTL_HOURS
	);
};
